import { MarginNote } from './db';
import { getGraphToken } from './auth';

/**
 * Version 3 - Collaborative Intelligence: OneDrive Sync Gate
 */
export const shouldSyncToVault = (): boolean => {
  // Sync all notes to the user's personal vault as a backup, 
  // regardless of their visibility settings in the app.
  return true;
};

/**
 * Converts a MarginNote object into an AI-ready Markdown document.
 */
const convertToMarkdown = (note: MarginNote): string => {
  const dateStr = new Date(note.createdAt).toLocaleString();
  const visibilityLabel = note.visibility.replace('_', ' ').toUpperCase();
  const globalLabel = note.isGlobal ? "Yes" : "No";
  const docLabel = note.isGlobal ? "N/A (Global Note)" : (note.documentName || "Unsaved Word Document");
  
  // Aggressive HTML cleaning: Strip all tags while preserving line breaks
  let cleanContent = (note.content || "")
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<p>/g, '').replace(/<\/p>/g, '\n\n')
    .replace(/<li>/g, '- ').replace(/<\/li>/g, '\n')
    .replace(/<[^>]*>/g, ''); // Strip all remaining tags (em, span, styles, etc.)

  let attachmentSection = "";
  if (note.attachmentUrl) {
    const isImage = note.attachmentType?.startsWith('image/');
    attachmentSection = `

---
### 📎 Attachments
${isImage ? `![${note.attachmentName}](${note.attachmentUrl})` : `[Download ${note.attachmentName}](${note.attachmentUrl})`}
`;
  }

  return `<!-- Note Type: Margin Pro Research Note -->
---
Title: ${note.label}
Workspace: ${visibilityLabel}
Global: ${globalLabel}
Source Document: ${docLabel}
ID: ${note.id}
Date: ${dateStr}
---

# ${note.label}

${cleanContent}
${attachmentSection}
---
*Created via Margin Pro for Microsoft Word*
`;
};

/**
 * Standardizes the filename used for OneDrive vault documents.
 * We use a combination of the label and the unique ID prefix to ensure
 * stability even if the main document is renamed.
 *
 * Files are written as `.txt` (not `.md`): Microsoft Search / M365 Copilot index
 * plain text reliably, but not Markdown — that's why Copilot couldn't find the notes.
 */
export const getVaultPath = (note: MarginNote): string => {
  const safeDocName = note.documentName
    ? note.documentName.replace(/[^a-z0-9 ]/gi, '_').trim()
    : 'Global_Notes';

  const safeLabel = note.label.replace(/[^a-z0-9]/gi, '_').substring(0, 30) || 'note';
  const fileName = `${safeLabel}_${note.id.substring(0, 4)}.txt`;

  return `MarginPro_Vault/${safeDocName}/${fileName}`;
};

export const syncNoteToVault = async (note: MarginNote, token: string | null): Promise<string> => {
  if (!shouldSyncToVault()) return "Skipped";

  if (!token) return "Auth Required";

  try {
    const vaultPath = getVaultPath(note);
    const mdContent = convertToMarkdown(note);

    // Direct PUT to root path - Graph API automatically creates missing intermediate folders!
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/drive/root:/${vaultPath}:/content`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'text/plain'
        },
        body: mdContent
      }
    );

    if (response.ok) {
      console.log(`Sync successful: ${vaultPath}`);
      return "Success";
    } else {
      if (response.status === 401) {
        console.warn("Microsoft Graph Token expired (401).");
        return "Auth Required";
      }

      const errorBody = await response.text();
      console.error(`Graph Sync Failed (${response.status}):`, errorBody);
      try {
        const errorJson = JSON.parse(errorBody);
        return `Failed (${response.status}): ${errorJson.error?.message || response.statusText}`;
      } catch (e) {
        return `Failed (${response.status}): ${response.statusText}`;
      }
    }
  } catch (error: any) {
    console.error("Network error during Graph sync:", error);
    return `Error: ${error.message}`;
  }
};

/**
 * Removes the corresponding Markdown note from the OneDrive vault.
 */
export const deleteNoteFromVault = async (note: MarginNote, token: string | null): Promise<string> => {
  if (!shouldSyncToVault()) return "Skipped";

  if (!token) return "Auth Required";

  try {
    const vaultPath = getVaultPath(note);
    console.log(`MarginPro: Attempting to delete file from OneDrive: ${vaultPath}`);
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/drive/root:/${vaultPath}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (response.ok || response.status === 404) {
      console.log(`MarginPro: Cloud delete successful for ${vaultPath} (Status: ${response.status})`);
      return "Success";
    } else {
      const errorBody = await response.text();
      console.error(`MarginPro: Graph Delete Failed (${response.status}):`, errorBody);
      return "Failed";
    }
  } catch (error: any) {
    console.error("MarginPro: Network error during Graph delete:", error);
    return "Error";
  }
};

/**
 * Compiles an array of MarginNotes into a single master document and saves it to OneDrive.
 * This provides a single, highly-reliable target for Copilot to ingest without searching folders.
 */
export const syncCompiledNotesToVault = async (notes: MarginNote[], token: string | null, docName: string): Promise<string> => {
  if (!token) return "Auth Required";
  if (notes.length === 0) return "Success"; // Nothing to compile

  try {
    const safeDocName = docName 
      ? docName.replace(/[^a-z0-9 ]/gi, '_').trim() 
      : 'Global_Notes';
    
    // Prefix with underscore forces it to the top of the folder list conceptually
    const vaultPath = `MarginPro_Vault/${safeDocName}/_Compiled_AI_Notes.txt`;

    const compiledContent = `Compiled Research Notes: ${docName || 'Global'}\n\n` +
      notes.map(note => convertToMarkdown(note)).join("\n\n");

    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/drive/root:/${vaultPath}:/content`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'text/plain'
        },
        body: compiledContent
      }
    );

    if (response.ok) {
      console.log(`Compiled Sync successful: ${vaultPath}`);
      return "Success";
    } else {
      return `Failed (${response.status})`;
    }
  } catch (error: any) {
    return `Error: ${error.message}`;
  }
};
